import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  Renderer2,
  AfterViewInit,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChatBubbleComponent } from '../chat-bubble/chat-bubble.component';
import { ChatInputComponent } from '../chat-input/chat-input.component';
import { ApplicationSummaryCardComponent } from '../application-summary-card/application-summary-card.component';
import { ApplicationService } from '../../services/application.service';
import { Subject, takeUntil, from, Observable } from 'rxjs';
import { ChatInterfaceService } from '../../services/chat-interface.service';

import { OnChanges, SimpleChanges } from '@angular/core';

@Component({
  selector: 'app-chat-interface',
  standalone: true,
  imports: [
    CommonModule,
    ChatBubbleComponent,
    ChatInputComponent,
    ApplicationSummaryCardComponent,
  ],
  templateUrl: './chat-interface.component.html',
  styleUrls: ['./chat-interface.component.css'],
})
export class ChatInterfaceComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input() applicationId: number | null = null;
  @Output() closeChat = new EventEmitter<void>();

  @ViewChild('historyModal', { static: true }) historyModal!: ElementRef;
  private modalInstance: any;
  private destroy$ = new Subject<void>();

  // Properties required by template
  showApplicationDetails: boolean = false;
  conversationHistory: any[] = [];
  showPrompts: boolean = false;
  coverLetterData: any = null;

  constructor(
    private chatInterfaceService: ChatInterfaceService,
    private applicationService: ApplicationService,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef
  ) {
    // Subscribe to application ID changes
    this.chatInterfaceService.currentApplicationId$
      .pipe(takeUntil(this.destroy$))
      .subscribe((id) => {
        if (id !== null) {
          this.applicationId = id;
          console.log(
            'Before fetching conversation history:',
            this.applicationId
          );
          this.fetchConversationHistory();
        }
      });

    // Subscribe to chat visibility changes
    this.chatInterfaceService.isVisible$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isVisible) => {
        if (isVisible) {
          this.showModal();
        } else {
          this.hideModal();
        }
      });
  }

  ngAfterViewInit(): void {
    console.log('ChatInterfaceComponent ngAfterViewInit', this.applicationId);
    this.showModal();
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log(
      'ChatInterfaceComponent ngOnChanges',
      changes,
      this.applicationId
    );
    if (changes['applicationId'] && changes['applicationId'].currentValue) {
      this.fetchConversationHistory();
    }
  }

  private onShown = () => {
    const modalElement = this.historyModal?.nativeElement;
    if (modalElement) {
      this.renderer.removeAttribute(modalElement, 'inert');
      setTimeout(() => {
        modalElement.focus();
        this.cdr.detectChanges();
      }, 0);
      modalElement.removeEventListener('shown.bs.modal', this.onShown);
    }
  };

  private hiddenHandler = () => {
    const modalElement = this.historyModal?.nativeElement;
    if (modalElement) {
      this.renderer.setAttribute(modalElement, 'inert', 'true');
      // Let Bootstrap handle backdrop removal
      modalElement.removeEventListener('hidden.bs.modal', this.hiddenHandler);
      this.closeChat.emit();
    }
  };

  showModal(): void {
    if (this.historyModal && this.historyModal.nativeElement) {
      const modalElement = this.historyModal.nativeElement;
      // Guard: Only show if not already open
      if (modalElement.classList.contains('show')) {
        return;
      }
      if (!this.modalInstance) {
        this.modalInstance = new (window as any).bootstrap.Modal(modalElement, {
          backdrop: 'static',
          keyboard: true,
          focus: true,
        });
      }
      // Attach event listeners only once
      modalElement.removeEventListener('shown.bs.modal', this.onShown);
      modalElement.addEventListener('shown.bs.modal', this.onShown);
      modalElement.removeEventListener('hidden.bs.modal', this.hiddenHandler);
      modalElement.addEventListener('hidden.bs.modal', this.hiddenHandler);
      this.modalInstance.show();
    }
  }

  onCloseClicked(): void {
    this.hideModal();
  }

  hideModal(): void {
    // Blur focus if inside modal to prevent ARIA/focus warnings
    if (
      document.activeElement &&
      this.historyModal?.nativeElement?.contains(document.activeElement)
    ) {
      (document.activeElement as HTMLElement).blur();
    }
    if (this.modalInstance) {
      this.modalInstance.hide();
    }
    // Do not manually remove backdrops; let Bootstrap handle it
  }

  // Methods required by template

  togglePrompts(): void {
    this.showPrompts = !this.showPrompts;
    this.cdr.markForCheck();
  }

  toggleApplicationDetails(): void {
    this.showApplicationDetails = !this.showApplicationDetails;
    this.cdr.markForCheck();
  }

  onMessageSent(response: any): void {
    // Implement message handling logic as needed
    const { message_type, message_text, tailored_cover_letter } = response;
    if (message_text) {
      this.conversationHistory.push({ message_type, message_text });

      if (tailored_cover_letter) {
        this.coverLetterData = tailored_cover_letter;
        console.log('Tailored Cover Letter:', this.coverLetterData);
      }
    }

    // if (!this.userScrolledUp) {
    //   this.scrollToBottom();
    // }
    this.cdr.markForCheck();
  }

  onApplicationCreated(newApplicationId: number): void {
    // Implement application creation logic as needed
    if (newApplicationId) {
      this.applicationId = newApplicationId;
      this.fetchConversationHistory();
      this.cdr.markForCheck();
      // this.chatInterfaceService.showChat(newApplicationId);
    } else {
      console.warn('No valid application ID received.');
    }
    this.cdr.markForCheck();
  }
  private fetchConversationHistory(): void {
    if (this.applicationId === null) {
      console.warn(
        'No application ID provided; unable to fetch conversation history.'
      );
      return;
    }

    const history$ = this.applicationService.getAllApplicationMessages(
      this.applicationId
    );
    const observable =
      history$ instanceof Promise
        ? from(history$)
        : (history$ as Observable<any>);

    observable.pipe(takeUntil(this.destroy$)).subscribe(
      (data) => {
        this.conversationHistory = Array.isArray(data)
          ? data.filter((entry) => entry.message_text)
          : [data];
        this.showModal();
        this.cdr.markForCheck();
      },
      (error) => {
        console.error('Error fetching conversation history:', error);
      }
    );
  }
  ngOnDestroy(): void {
    // Hide modal and clean up
    this.hideModal();
    // Dispose modal instance if exists
    if (
      this.modalInstance &&
      typeof this.modalInstance.dispose === 'function'
    ) {
      this.modalInstance.dispose();
      this.modalInstance = null;
    }
    // Remove event listeners
    const modalElement = this.historyModal?.nativeElement;
    if (modalElement) {
      modalElement.removeEventListener('shown.bs.modal', this.onShown);
      modalElement.removeEventListener('hidden.bs.modal', this.hiddenHandler);
    }
  }
}
